"use client";

import { memo } from "react";
import { useScenePause } from "./useScenePause";

/**
 * Animated SVG illustration for the AI-chat flow tile on the landing page.
 *
 * Reads as a real conversation beat: the user question slides in, the AI
 * bubble appears with a live TYPING INDICATOR (three bouncing dots), then
 * the answer lines BUILD THEMSELVES in - each bar scales in from the left
 * (blockBuild ported to scaleX) staggered like streaming text - while the
 * sparkle pops and keeps breathing. The scene holds, fades, and pauses
 * before looping (7.5s).
 *
 * Techniques: blockBuild-style scaleX bars, sCheckPop-style sparkle pop,
 * an independent short bounce loop for the typing dots gated by a parent
 * opacity window, and the .22,1,.36,1 signature ease on every reveal.
 * transform/opacity only; memo() wrapped; reduced-motion resting frame.
 */
function ChatFlowAnimation() {
  const sceneRef = useScenePause<SVGSVGElement>();
  const C_PAPER = "var(--color-paper-soft)";
  const C_RULE = "var(--color-rule)";
  const C_INK_MUTED = "var(--color-ink-muted)";
  const C_INK_FAINT = "var(--color-ink-faint)";
  const C_ACCENT = "var(--color-accent)";

  return (
    <svg ref={sceneRef} viewBox="0 0 200 160" className="scene-svg w-full h-44" aria-hidden="true">
      {/* user question bubble (right, accent) */}
      <g className="cf-q">
        <rect x="74" y="20" width="114" height="36" rx="11" fill={C_ACCENT} />
        <rect x="86" y="31" width="76" height="4.5" rx="2.25" fill="#ffffff" fillOpacity="0.9" />
        <rect x="86" y="41" width="48" height="4.5" rx="2.25" fill="#ffffff" fillOpacity="0.55" />
      </g>

      {/* AI answer bubble (left, paper) */}
      <g className="cf-a">
        <rect x="12" y="78" width="148" height="58" rx="11" fill={C_PAPER} stroke={C_RULE} strokeWidth="1" />
        <g className="cf-sparkPop">
          <path
            className="cf-spark"
            d="M32 96 l2.6 6.4 l6.4 2.6 l-6.4 2.6 l-2.6 6.4 l-2.6 -6.4 l-6.4 -2.6 l6.4 -2.6 Z"
            fill={C_ACCENT}
          />
        </g>

        {/* typing indicator - three dots bounce while the AI "thinks";
            gated by the cf-dots opacity window, each dot runs its own
            short bounce loop with a stagger */}
        <g className="cf-dots" fill={C_INK_FAINT}>
          <circle className="cf-dot cf-dot-1" cx="56" cy="106" r="3" />
          <circle className="cf-dot cf-dot-2" cx="67" cy="106" r="3" />
          <circle className="cf-dot cf-dot-3" cx="78" cy="106" r="3" />
        </g>

        {/* answer lines - build in from the left, staggered like text
            streaming in */}
        <rect className="cf-l1" x="50" y="92" width="96" height="5" rx="2.5" fill={C_INK_MUTED} />
        <rect className="cf-l2" x="50" y="103" width="84" height="5" rx="2.5" fill={C_INK_FAINT} />
        <rect className="cf-l3" x="50" y="114" width="62" height="5" rx="2.5" fill={C_INK_FAINT} />
      </g>

      <style>{`
        /* 7.5s loop:
             2-10%   question slides in
             16-22%  AI bubble + sparkle pop in
             22-42%  typing dots bounce
             44-64%  answer lines build in (staggered scaleX)
             64-90%  hold
             90-96%  fade; 96-100% quiet pause */
        @keyframes cfQ {
          0%, 2%    { opacity: 0; transform: translateY(8px); }
          10%       { opacity: 1; transform: translateY(0); }
          90%       { opacity: 1; transform: translateY(0); }
          96%, 100% { opacity: 0; transform: translateY(0); }
        }
        @keyframes cfA {
          0%, 14%   { opacity: 0; transform: translateY(10px); }
          22%       { opacity: 1; transform: translateY(0); }
          90%       { opacity: 1; transform: translateY(0); }
          96%, 100% { opacity: 0; transform: translateY(0); }
        }
        @keyframes cfSparkPop {
          0%, 16%   { transform: scale(0.3); opacity: 0; }
          24%       { transform: scale(1.2); opacity: 1; }
          30%, 100% { transform: scale(1); opacity: 1; }
        }
        @keyframes cfSpark {
          0%, 100% { transform: scale(0.9) rotate(0); opacity: .75; }
          50%      { transform: scale(1.08) rotate(15deg); opacity: 1; }
        }
        @keyframes cfDots {
          0%, 20%   { opacity: 0; }
          24%, 40%  { opacity: 1; }
          44%, 100% { opacity: 0; }
        }
        @keyframes cfDotBounce {
          0%, 100% { transform: translateY(0); }
          30%      { transform: translateY(-3.5px); }
          60%      { transform: translateY(0); }
        }
        @keyframes cfL1 {
          0%, 44%   { opacity: 0; transform: scaleX(0); }
          52%, 100% { opacity: 1; transform: scaleX(1); }
        }
        @keyframes cfL2 {
          0%, 50%   { opacity: 0; transform: scaleX(0); }
          58%, 100% { opacity: 1; transform: scaleX(1); }
        }
        @keyframes cfL3 {
          0%, 56%   { opacity: 0; transform: scaleX(0); }
          64%, 100% { opacity: 1; transform: scaleX(1); }
        }
        .cf-q        { transform-box: fill-box; animation: cfQ 7.5s cubic-bezier(.22,1,.36,1) infinite; }
        .cf-a        { transform-box: fill-box; animation: cfA 7.5s cubic-bezier(.22,1,.36,1) infinite; }
        .cf-sparkPop { transform-box: fill-box; transform-origin: center; animation: cfSparkPop 7.5s cubic-bezier(.22,1,.36,1) infinite; }
        .cf-spark    { transform-box: fill-box; transform-origin: center; animation: cfSpark 2.6s ease-in-out infinite; }
        .cf-dots     { animation: cfDots 7.5s ease-in-out infinite; }
        .cf-dot      { transform-box: fill-box; transform-origin: center; animation: cfDotBounce 1s ease-in-out infinite; }
        .cf-dot-2    { animation-delay: .16s; }
        .cf-dot-3    { animation-delay: .32s; }
        .cf-l1       { transform-box: fill-box; transform-origin: left center; animation: cfL1 7.5s cubic-bezier(.22,1,.36,1) infinite; }
        .cf-l2       { transform-box: fill-box; transform-origin: left center; animation: cfL2 7.5s cubic-bezier(.22,1,.36,1) infinite; }
        .cf-l3       { transform-box: fill-box; transform-origin: left center; animation: cfL3 7.5s cubic-bezier(.22,1,.36,1) infinite; }
        @media (prefers-reduced-motion: reduce) {
          .cf-q, .cf-a, .cf-sparkPop, .cf-spark, .cf-dots, .cf-dot, .cf-l1, .cf-l2, .cf-l3 { animation: none; }
          .cf-q, .cf-a, .cf-sparkPop, .cf-l1, .cf-l2, .cf-l3 { opacity: 1; transform: none; }
          .cf-dots { opacity: 0; }
        }
      `}</style>
    </svg>
  );
}

export default memo(ChatFlowAnimation);
